import { motion, type Transition } from 'motion/react';
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from 'v5/platform/dom/cn';

export interface AnimatedHeightProps {
  children: ReactNode;
  className?: string;
  /**
   * Classes of the measured content box. Put size limits here (e.g. `max-h-*` / `min-h-*` with a
   * flex column), not on an ancestor: the content then settles at its final size at once and only
   * gets revealed or clipped while the height moves, instead of being squeezed into a scrollbar.
   */
  contentClassName?: string;
  transition?: Transition;
  /**
   * `always` (default): the box always clips — a dialog body whose content scrolls or is sized.
   * `while-resizing`: clips only while the height moves, so shadows, focus rings and content
   * bleeding past the box stay visible at rest (form units, list rows); give such content room at
   * the sides meanwhile (`-mx-*` here, `px-*` on the content). It clips with `overflow: clip`,
   * which cannot scroll: focusing a field below the edge meanwhile does not shift the content up.
   */
  clip?: 'always' | 'while-resizing';
}

/** The easing shared by stage slides and height changes. */
export const stageTransition: Transition = { duration: 0.4, ease: 'circInOut' };

/**
 * Follows the height of its content with an animation — a dialog switching steps, a panel swapping
 * its body. Only this box resizes; the content keeps its natural size and is clipped.
 */
export function AnimatedHeight({
  children,
  className,
  contentClassName,
  transition = stageTransition,
  clip = 'always'
}: AnimatedHeightProps) {
  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [resizing, setResizing] = useState(false);
  // Motion resolves the `auto` start of the first change from the bounding box, which an ancestor's
  // transform shrinks (a dialog opening with `zoom-in-95`); animating from it clips the content
  // until the easing catches up. The first measured height is applied at once instead.
  const hasHeight = useRef(false);
  useEffect(() => {
    if (height !== null) hasHeight.current = true;
  }, [height]);

  useLayoutEffect(() => {
    if (!content) return;
    const measure = () => setHeight(content.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(content);
    return () => observer.disconnect();
  }, [content]);

  return (
    <motion.div
      className={cn(
        'rounded-[inherit]',
        clip === 'always'
          ? 'overflow-hidden'
          : resizing
            ? 'overflow-clip'
            : 'overflow-visible',
        className
      )}
      // the first measurement is where it starts, not something to animate to
      initial={false}
      animate={{ height: height ?? 'auto' }}
      transition={hasHeight.current ? transition : { duration: 0 }}
      onAnimationStart={() => setResizing(true)}
      onAnimationComplete={() => setResizing(false)}
    >
      <div ref={setContent} className={cn('relative', contentClassName)}>
        {children}
      </div>
    </motion.div>
  );
}
